import { Box } from '@mui/material';
import { FC } from 'react';
import {
  Cell,
  Label,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { theme } from '../../../../theme';
import { DoughnutChartInsideLabel } from './insideLabel';
import { DoughnutChartLegend, FormatLegendValueFunction } from './legend';
import { DoughnutChartTooltip } from './tooltip';

type WithTooltip =
  | { withTooltip?: false }
  | {
      withTooltip: true;
      buildTooltipText?: (value?: number) => string;
    };

type WithLegend =
  | { withLegend?: false }
  | {
      withLegend: true;
      legendHeadline?: string;
      formatLegendValue?: FormatLegendValueFunction;
    };

type Props = {
  /**
   * We decided to use a more restricting type than the Array<any> that
   * PieProps['data'] provides, because we have no other use case than
   * number values.
   */
  data: Array<{ value: number; name: string }>;
  variant?: 'overlapping' | 'padding';
  colors?: Array<string>;
  startAngle?: number;
  endAngle?: number;
  insideLabel?: {
    headline: string;
    description?: string;
  };
} & WithTooltip &
  WithLegend;

export const COLORS = [
  theme.palette.mint.main,
  theme.palette.heaven.main,
  theme.palette.secondary.main,
  theme.palette.primary.main,
  '#31DFFF',
  '#80FF7B',
  '#FFFF8D',
  '#00B5A4',
  '#A36AFF',
  '#FF80AB',
  '#76CAFF',
  '#41DB3B',
  '#FFFF00'
];

export const DoughnutChart: FC<Props> = ({
  data,
  variant = 'padding',
  colors = COLORS,
  insideLabel,
  startAngle,
  endAngle,
  ...otherProps
}) => {
  const filteredData = data.filter((value) => value.value > 0);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        '& > div:first-of-type': {
          alignSelf: 'center',
          justifySelf: 'center'
        }
      }}
    >
      <ResponsiveContainer minWidth={'60px'} aspect={1}>
        <RechartsPieChart>
          <Pie
            data={filteredData}
            innerRadius={'80%'}
            outerRadius={'100%'}
            paddingAngle={variant === 'padding' ? 8 : -8}
            dataKey="value"
            stroke="none"
            cornerRadius={'100%'}
            startAngle={startAngle}
            endAngle={endAngle}
          >
            {insideLabel && (
              <Label
                position={'center'}
                content={(props) => (
                  <DoughnutChartInsideLabel
                    viewBox={
                      props.viewBox as {
                        cx: number;
                        cy: number;
                      }
                    }
                    headline={insideLabel.headline}
                    description={insideLabel?.description}
                  />
                )}
              />
            )}
            {data?.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          {otherProps.withTooltip && (
            <Tooltip<number, string>
              content={(props) => (
                <DoughnutChartTooltip
                  active={props.active}
                  payload={props.payload}
                  buildTooltipText={otherProps.buildTooltipText}
                />
              )}
            />
          )}
        </RechartsPieChart>
      </ResponsiveContainer>
      {otherProps.withLegend && (
        <DoughnutChartLegend
          headline={otherProps.legendHeadline}
          formatLegendValue={otherProps.formatLegendValue}
          labels={filteredData.map((d, index) => ({ ...d, color: COLORS[index] }))}
        />
      )}
    </Box>
  );
};
